"use client";
import type { StorageAdapter } from "./adapter";
import type { AssetMeta, Doc, DocID, DocSummary } from "./types";

type RPCRequest = {
  type: "ploom:rpc:request";
  id: string;
  method:
    | "list"
    | "create"
    | "read"
    | "save"
    | "remove"
    | "putAsset"
    | "getAsset"
    | "gcAssets";
  payload?: unknown;
};

type RPCResponse = {
  type: "ploom:rpc:response";
  id: string;
  ok: boolean;
  result?: unknown;
  error?: string;
};

function uuid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export class HostEmbeddedStorage implements StorageAdapter {
  private target: Window;
  private origin: string | "*";
  private pending = new Map<string, (res: RPCResponse) => void>();
  private onMsg = (e: MessageEvent) => {
    const data = e.data as RPCResponse;
    if (!data || typeof data !== "object") return;
    if (data.type !== "ploom:rpc:response") return;
    const resolve = this.pending.get(data.id);
    if (resolve) {
      this.pending.delete(data.id);
      resolve(data);
    }
  };

  constructor(target: Window, origin: string | "*" = "*") {
    this.target = target;
    this.origin = origin;
    window.addEventListener("message", this.onMsg);
  }

  private async call<T = unknown>(method: RPCRequest["method"], payload?: unknown, timeoutMs = 8000): Promise<T> {
    const id = uuid();
    const req: RPCRequest = { type: "ploom:rpc:request", id, method, payload };
    const p = new Promise<T>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`RPC timeout for ${method}`));
      }, timeoutMs);
      this.pending.set(id, (res) => {
        window.clearTimeout(timer);
        if (!res.ok) reject(new Error(res.error || "RPC error"));
        else resolve(res.result as T);
      });
    });
    this.target.postMessage(req, this.origin);
    return p;
  }

  async list(): Promise<DocSummary[]> {
    return this.call<DocSummary[]>("list");
  }
  async create(init?: Partial<Doc>): Promise<Doc> {
    return this.call<Doc>("create", { init });
  }
  async read(id: DocID): Promise<Doc | null> {
    return this.call<Doc | null>("read", { id });
  }
  async save(id: DocID, patch: Partial<Doc>): Promise<Doc> {
    return this.call<Doc>("save", { id, patch });
  }
  async remove(id: DocID): Promise<void> {
    await this.call("remove", { id });
  }
  async putAsset(blob: Blob): Promise<{ blobKey: string; meta: AssetMeta }> {
    // Transfer as ArrayBuffer to avoid cross-origin Blob issues
    const buf = await blob.arrayBuffer();
    const payload = { buffer: buf, type: blob.type };
    return this.call<{ blobKey: string; meta: AssetMeta }>("putAsset", payload);
  }
  async getAsset(blobKey: string): Promise<Blob | null> {
    const res = await this.call<{ buffer: ArrayBuffer; type: string } | null>("getAsset", { blobKey });
    return res ? new Blob([res.buffer], { type: res.type }) : null;
  }
  async gcAssets(): Promise<void> {
    await this.call("gcAssets");
  }

  dispose() {
    window.removeEventListener("message", this.onMsg);
    this.pending.clear();
  }
}

export async function probeHostRPC(timeoutMs = 600): Promise<boolean> {
  try {
    if (window.top === window) return false;
    const id = uuid();
    const req = { type: "ploom:rpc:hello", id } as const;
    const p = new Promise<boolean>((resolve) => {
      const on = (e: MessageEvent) => {
        const data = e.data as { type: string; id: string };
        if (data?.type === "ploom:rpc:hello:ack" && data.id === id) {
          window.removeEventListener("message", on);
          resolve(true);
        }
      };
      window.addEventListener("message", on);
      window.setTimeout(() => {
        window.removeEventListener("message", on);
        resolve(false);
      }, timeoutMs);
    });
    window.parent?.postMessage(req, "*");
    return await p;
  } catch {
    return false;
  }
}

